import { checkLogoFile, checkPhotoFile, PHOTO_BUCKET } from "@/modules/catalog/surfaces";
import { supabase } from "~/auth/supabase";

/**
 * The clinic's pictures, uploaded from the browser **as the signed-in member**
 * (04-architecture.md §6, the one narrow storage exception).
 *
 * R22 removed the server action that used to upload the logo and nothing had
 * taken its place, so a picked logo was silently dropped. R27 restores it by
 * the same route the photos take (H59): supabase-js holds the member's session,
 * and each bucket's policy admits a write only under the member's own
 * business folder. No table is touched here; the save that follows is the
 * ordinary API call.
 */

const LOGO_BUCKET = "business-logos";

export type Upload = { ok: true; url: string; path: string } | { ok: false };

async function upload(
  bucket: string,
  path: string,
  file: File,
  contentType: string,
  upsert: boolean,
): Promise<Upload> {
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, file, { contentType, upsert, cacheControl: "3600" });
  if (error) return { ok: false };
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return { ok: true, url: data.publicUrl, path };
}

/**
 * The logo always lives at one address per type and is overwritten, so a
 * replaced logo leaves nothing behind; `?v=` makes every cache let go of the
 * old picture.
 */
export async function uploadLogo(businessId: string, file: File): Promise<Upload> {
  const check = checkLogoFile(file);
  if (!check.ok) return { ok: false };
  const result = await upload(
    LOGO_BUCKET,
    `${businessId}/logo.${check.extension}`,
    file,
    check.contentType,
    true,
  );
  return result.ok ? { ...result, url: `${result.url}?v=${Date.now()}` } : result;
}

/** A photo gets a name of its own, so a reorder never overwrites a file. */
export async function uploadPhoto(businessId: string, file: File): Promise<Upload> {
  const check = checkPhotoFile(file);
  if (!check.ok) return { ok: false };
  return upload(
    PHOTO_BUCKET,
    `${businessId}/${crypto.randomUUID()}.${check.extension}`,
    file,
    check.contentType,
    false,
  );
}

/**
 * Delete photos by public address, best effort. A failure leaves a file
 * nothing points at, never a broken photo, which is why nobody is told.
 */
export async function deletePhotos(urls: readonly string[]): Promise<void> {
  const marker = `/object/public/${PHOTO_BUCKET}/`;
  const paths = urls
    .map((url) => url.split(marker)[1]?.split(/[?#]/, 1)[0])
    .filter((path): path is string => Boolean(path));
  if (paths.length === 0) return;
  await supabase.storage.from(PHOTO_BUCKET).remove(paths).catch(() => undefined);
}
